import type { ReactNode } from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { noop } from 'lodash';
import { useIntersectionObserverContext } from '../useIntersectionObserver/useIntersectionObserver';
import type { MeasurementId } from './useSkeletonContext';
import { SkeletonContext } from './useSkeletonContext';

export type UseSkeletonOpts = {
  /** unique id for caching measurements */
  id?: MeasurementId;
  /** never show the skeleton again after first load */
  lazy?: boolean;
  /** adjust the viewport to accommodate layout shifts */
  scrollAnchor?: boolean;
};

export type MountType = `firstLoad` | `added` | null;

/**
 * a hook which tells a component when to render a skeleton and the dimensions
 * that skeleton should be
 */
export const useSkeleton = ({ id, lazy }: UseSkeletonOpts = {}) => {
  const [isIntersecting, setIsIntersecting] = useState<boolean | null>(null);

  const { measurements, disableSkeletons } = useContext(SkeletonContext);

  const hasIntersected = useRef(false);
  const observationCountRef = useRef(0);
  const wasIntersectingOnMount = useRef<boolean | null>(null);

  const viewport = useRef<Element | null>(null);
  const measurement = useRef<DOMRectReadOnly | undefined>(
    id ? measurements.get(id) : undefined
  );

  const callback = useCallback(
    (entry: IntersectionObserverEntry, observer: IntersectionObserver) => {
      viewport.current = observer.root as Element;

      /**
       * measure the item on callback
       * @todo (CORE-5055): investigate inconsistent height measurements between
       * entry.boundingClientRect.height and entry.target.offsetHeight.
       */
      measurement.current = entry.boundingClientRect;

      /** if an id has been provided, cache the measurement in the skeleton context */
      if (id) measurements.set(id, measurement.current);

      if (entry?.isIntersecting) {
        //entering
        hasIntersected.current = true;
      } else {
        //exiting
      }

      if (wasIntersectingOnMount.current === null)
        wasIntersectingOnMount.current = entry.isIntersecting;

      observationCountRef.current += 1;

      setIsIntersecting(entry?.isIntersecting || false);
    },
    [id, measurements]
  );

  let showSkeleton = false;
  if (!disableSkeletons)
    showSkeleton = lazy ? !hasIntersected.current : !isIntersecting;

  const { ref: ioRef } = useIntersectionObserverContext({
    callback,
    skipUpdate: true,
  });

  const observationCount = observationCountRef.current;

  const firstLoadRef = useRef(measurements.size === 0);
  useEffect(() => {
    firstLoadRef.current = measurements.size === 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let mountType: MountType = null;
  if (observationCount < 1) {
    if (firstLoadRef.current) mountType = `firstLoad`;
    else mountType = `added`;
  }

  return {
    /** should we render the skeleton? */
    showSkeleton,
    /** pass to the component and skeleton to be observed */
    ref: disableSkeletons ? noop : ioRef,
    /** the last known DOMRect. this will be null initially */
    measurement: measurement.current,
    /** the number of times the item has been observed */
    observationCount,
    mountType,
  };
};

/** renderprop version of `useSkeleton` */
export const WithSkeleton: React.FC<
  React.PropsWithChildren<
    UseSkeletonOpts & {
      children: (result: ReturnType<typeof useSkeleton>) => ReactNode;
    }
  >
> = (props) => {
  const { children, ...opts } = props;
  const result = useSkeleton(opts);

  return <>{children(result)}</>;
};
